import { Allotment } from "allotment";
import clsx from "clsx";
import React from "react";
import Icon from "../Icon/Icon";
import "allotment/dist/style.css";
import { createPluggable } from "pluggable-react";
import Button from "../Button/Button";
import styles from "./SplitPane.module.scss";

export interface SplitPaneProps {
  children: React.ReactNode;
  direction?: "horizontal" | "vertical";
  split?: "vertical" | "horizontal";
  gutterSize?: number;
  className?: string;
  style?: React.CSSProperties;
  onDrag?: (sizes: any) => void;
  onDragEnd?: (sizes: any) => void;
}

export interface SplitPanePaneProps extends React.ComponentProps<typeof Allotment.Pane> {
}

// Internal component identifiers
const COLLAPSIBLE_PANE_TYPE = Symbol("SplitPaneCollapsiblePane");
const COLLAPSIBLE_PANE_HEADER_TYPE = Symbol("SplitPaneCollapsiblePaneHeader");
const COLLAPSIBLE_PANE_CONTENT_TYPE = Symbol("SplitPaneCollapsiblePaneContent");
const COLLAPSIBLE_PANE_HEADER_ACTIONS_TYPE = Symbol("SplitPaneCollapsiblePaneHeaderActions");

export interface SplitPaneCollapsiblePaneProps {
  children: React.ReactNode;
  header?: string; // Deprecated: use CollapsiblePane.Header with CollapsiblePane.Title instead
  persistId?: string;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  minSize?: number | string;
  maxSize?: number | string;
  preferredSize?: number | string;
  collapsedLabel?: React.ReactNode;
}

const SplitPaneCollapsiblePane = (props: SplitPaneCollapsiblePaneProps) => {
  // This component is just a marker - actual rendering happens in DefaultSplitPane
  return null as any;
};
(SplitPaneCollapsiblePane as any).__type = COLLAPSIBLE_PANE_TYPE;

export interface SplitPaneCollapsiblePaneHeaderProps {
  children?: React.ReactNode;
  className?: string;
}

const SplitPaneCollapsiblePaneHeader = (props: SplitPaneCollapsiblePaneHeaderProps) => {
  return null as any;
};
(SplitPaneCollapsiblePaneHeader as any).__type = COLLAPSIBLE_PANE_HEADER_TYPE;

export interface SplitPaneCollapsiblePaneContentProps {
  children: React.ReactNode;
  className?: string;
}

const SplitPaneCollapsiblePaneContent = (props: SplitPaneCollapsiblePaneContentProps) => {
  return null as any;
};
(SplitPaneCollapsiblePaneContent as any).__type = COLLAPSIBLE_PANE_CONTENT_TYPE;

type CollapsiblePaneContextValue = {
  collapsed: boolean;
  toggleCollapsed: () => void;
};

const CollapsiblePaneContext = React.createContext<CollapsiblePaneContextValue | null>(null);

export interface SplitPaneCollapsiblePaneToggleButtonProps {
  className?: string;
  ariaLabel?: string;
  size?: "sm" | "md" | "lg";
}

const SplitPaneCollapsiblePaneToggleButton = ({
  className,
  ariaLabel,
  size = undefined,
}: SplitPaneCollapsiblePaneToggleButtonProps) => {
  const ctx = React.useContext(CollapsiblePaneContext);
  if (!ctx) return null;
  return (
    <Button icon
      size={size}
      onClick={ctx.toggleCollapsed}
      className={clsx(styles.collapsiblePaneToggle, className)}
      label={ariaLabel || (ctx.collapsed ? "Expand" : "Collapse")}
    >
      {ctx.collapsed ? <Icon name="expand-pane" /> : <Icon name="collapse-pane" />}
    </Button>
  );
};

export interface SplitPaneCollapsiblePaneTitleProps {
  children: React.ReactNode;
  className?: string;
}

const SplitPaneCollapsiblePaneTitle = (props: SplitPaneCollapsiblePaneTitleProps) => {
  const { children, className } = props;
  return <div className={clsx(styles.collapsiblePaneHeaderText, className)}>{children}</div>;
};

export interface SplitPaneCollapsiblePaneHeaderActionsProps {
  children: React.ReactNode;
  className?: string;
}

const SplitPaneCollapsiblePaneHeaderActions = (props: SplitPaneCollapsiblePaneHeaderActionsProps) => {
  const { children, className } = props;
  return (
    <div className={clsx(styles.collapsiblePaneHeaderActions, className)}>
      {children}
    </div>
  );
};
(SplitPaneCollapsiblePaneHeaderActions as any).__type = COLLAPSIBLE_PANE_HEADER_ACTIONS_TYPE;

const DefaultSplitPane = (props: SplitPaneProps) => {
  const { children, direction = "horizontal", split, className, style, onDrag, onDragEnd } = props;

  const splitProp = split ?? (direction === "horizontal" ? "vertical" : "horizontal");
  const vertical = splitProp === "horizontal";

  // Track collapsed state for each collapsible pane by index (for uncontrolled components)
  const [ collapsibleStates, setCollapsibleState ] = React.useState<Record<number, boolean>>({});

  const readPersistedCollapsed = React.useCallback((persistId: string): boolean | null => {
    if (typeof window === "undefined") return null;
    try {
      const value = window.localStorage.getItem(`deci.splitpane.${persistId}.collapsed`);
      if (value === null) return null;
      return value === "true";
    } catch {
      return null;
    }
  }, []);

  const writePersistedCollapsed = React.useCallback((persistId: string, collapsed: boolean) => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(`deci.splitpane.${persistId}.collapsed`, String(collapsed));
    } catch {
      // noop if storage is unavailable
    }
  }, []);

  // Process children and replace CollapsiblePane with Allotment.Pane
  const processedChildren = React.useMemo(() => {
    return React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child)) {
        return child;
      }

      // Check if this is a CollapsiblePane
      const isCollapsiblePane = (child.type as any)?.__type === COLLAPSIBLE_PANE_TYPE;

      if (isCollapsiblePane) {
        const collapsibleProps = child.props as SplitPaneCollapsiblePaneProps;
        const defaultCollapsed = collapsibleProps.defaultCollapsed ?? false;
        const persistedCollapsed = collapsibleProps.persistId ? readPersistedCollapsed(collapsibleProps.persistId) : null;
        const initialCollapsed = persistedCollapsed ?? defaultCollapsed;
        const isControlled = collapsibleProps.collapsed !== undefined;

        const collapsed = isControlled
          ? collapsibleProps.collapsed
          : (collapsibleStates[index] ?? initialCollapsed);
        const toggleCollapsed = () => {
          const newCollapsed = !collapsed;
          if (collapsibleProps.persistId) {
            writePersistedCollapsed(collapsibleProps.persistId, newCollapsed);
          }
          if (isControlled && collapsibleProps.onCollapsedChange) {
            collapsibleProps.onCollapsedChange(newCollapsed);
          } else {
            setCollapsibleState(state => ({ ...state, [index]: newCollapsed }));
          }
        };

        // Calculate pane props based on collapsed state
        const paneProps: Partial<React.ComponentProps<typeof Allotment.Pane>> = collapsed
          ? {
            minSize: 50,
            maxSize: 50,
            preferredSize: 50,
          }
          : {
            minSize: typeof collapsibleProps.minSize === "string" ? undefined : collapsibleProps.minSize,
            maxSize: typeof collapsibleProps.maxSize === "string" ? undefined : collapsibleProps.maxSize,
            preferredSize: collapsibleProps.preferredSize,
          };

        // Process children to extract Header and Content
        let customHeaderProps: SplitPaneCollapsiblePaneHeaderProps | null = null;
        let contentProps: SplitPaneCollapsiblePaneContentProps | null = null;
        const otherChildren: React.ReactNode[] = [];

        React.Children.forEach(collapsibleProps.children, (child) => {
          if (!React.isValidElement(child)) {
            otherChildren.push(child);
            return;
          }

          const childType = (child.type as any)?.__type;
          if (childType === COLLAPSIBLE_PANE_HEADER_TYPE) {
            customHeaderProps = child.props as SplitPaneCollapsiblePaneHeaderProps;
          } else if (childType === COLLAPSIBLE_PANE_CONTENT_TYPE) {
            contentProps = child.props as SplitPaneCollapsiblePaneContentProps;
          } else {
            otherChildren.push(child);
          }
        });

        // If no Content component found, use all non-recognized children as content
        if (!contentProps && otherChildren.length > 0) {
          contentProps = {
            children: otherChildren.length === 1 ? otherChildren[0] : <>{otherChildren}</>,
          };
        }

        // Replace CollapsiblePane with Allotment.Pane
        return (
          <Allotment.Pane key={`${paneProps.key || index}-${collapsed}`} {...paneProps}>
            <CollapsiblePaneContent
              header={collapsibleProps.header}
              collapsed={collapsed ?? false}
              toggleCollapsed={toggleCollapsed}
              collapsedLabel={collapsibleProps.collapsedLabel}
              customHeaderProps={customHeaderProps || undefined}
              contentProps={contentProps}
            />
          </Allotment.Pane>
        );
      }

      return child;
    });
  }, [ children, collapsibleStates, readPersistedCollapsed, writePersistedCollapsed ]);

  return (
    <div
      data-deci-split-pane="true"
      data-direction={direction}
      className={clsx(styles.splitPane, className)}
      style={style}
    >
      <Allotment
        vertical={vertical}
        onChange={onDrag as any}
        onDragEnd={onDragEnd as any}
        proportionalLayout={true}
      >
        {processedChildren}
      </Allotment>
    </div>
  );
};

interface CollapsiblePaneContentProps {
  header?: string; // Deprecated: for backward compatibility
  collapsed: boolean;
  toggleCollapsed: () => void;
  collapsedLabel?: React.ReactNode;
  customHeaderProps?: SplitPaneCollapsiblePaneHeaderProps;
  contentProps: SplitPaneCollapsiblePaneContentProps | null;
}

const CollapsiblePaneContent = ({
                                  header,
                                  collapsed,
                                  toggleCollapsed,
                                  collapsedLabel,
                                  customHeaderProps,
                                  contentProps,
                                }: CollapsiblePaneContentProps) => {
  const defaultToggleButton = (
    <Button icon
      onClick={toggleCollapsed}
      className={styles.collapsiblePaneToggle}
      label={collapsed ? "Expand" : "Collapse"}
    >
      {collapsed ? <Icon name="expand-pane" /> : <Icon name="collapse-pane" />}
    </Button>
  );

  const hasCustomHeader = customHeaderProps !== null && customHeaderProps !== undefined;
  const hasDefaultHeader = (header || collapsedLabel) && !hasCustomHeader;

  return (
    <CollapsiblePaneContext.Provider value={{ collapsed, toggleCollapsed }}>
      <div className={clsx(styles.collapsiblePane)} data-collapsed={collapsed ? "true" : "false"}>
        {collapsed ? (
          // When collapsed, show only the label at bottom and toggle button at top (no header structure)
          <>
            {defaultToggleButton}
            {(collapsedLabel || header) && (
              <div className={styles.collapsiblePaneLabel}>
                {collapsedLabel || header}
              </div>
            )}
          </>
        ) : (
          // When expanded, show the full header
          <>
            {hasCustomHeader ? (
              <div {...customHeaderProps} className={clsx(styles.collapsiblePaneHeader, customHeaderProps?.className)}>
                {customHeaderProps?.children}
              </div>
            ) : hasDefaultHeader ? (
              <div className={clsx(styles.collapsiblePaneHeader)}>
                <div className={styles.collapsiblePaneHeaderLeft}>
                  {typeof header === "string" ? (
                    <SplitPaneCollapsiblePaneTitle>{header}</SplitPaneCollapsiblePaneTitle>
                  ) : header}
                </div>
                <div className={styles.collapsiblePaneHeaderRight}>
                  {defaultToggleButton}
                </div>
              </div>
            ) : null}
            {contentProps && (
              <div className={clsx(styles.collapsiblePaneContent, contentProps.className)}>
                {contentProps.children}
              </div>
            )}
          </>
        )}
      </div>
    </CollapsiblePaneContext.Provider>
  );
};

const SplitPane = createPluggable<SplitPaneProps>("deci.splitPane", DefaultSplitPane);

export default Object.assign(SplitPane, {
  Pane: Allotment.Pane,
  CollapsiblePane: Object.assign(SplitPaneCollapsiblePane, {
    Header: SplitPaneCollapsiblePaneHeader,
    Content: SplitPaneCollapsiblePaneContent,
    Title: SplitPaneCollapsiblePaneTitle,
    HeaderActions: SplitPaneCollapsiblePaneHeaderActions,
    ToggleButton: SplitPaneCollapsiblePaneToggleButton,
  }),
});
